import { parseApiDate } from '../../../lib/format';
import type { Doctor, MedicalConsultation, SignerSummary } from '../../../types/api';

/** The signature fields a record carries (consultation, or a re-issued prescription). */
export interface SignatureFields {
  signature_method?: MedicalConsultation['signature_method'];
  signed_at?: string | null;
  signer?: SignerSummary | null;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** dd/mm/yyyy HH:mm in local time; the PDF prints the same pattern. */
export function formatSignedAt(value: string | null | undefined): string | null {
  const date = parseApiDate(value);
  if (!date) return null;
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * clinical-record-signing R13, same wording as the backend's
 * `pdf/partials/delegated-signature.blade.php`: a record signed by
 * delegation names the assistant and the responsible doctor. Null for a
 * personal signature, which prints no line at all.
 */
export function delegatedSignatureLine(
  signed: SignatureFields | null | undefined,
  doctor: Pick<Doctor, 'first_name' | 'last_name'> | null | undefined,
): string | null {
  if (!signed || signed.signature_method !== 'delegated') return null;
  const assistant = [signed.signer?.name, signed.signer?.lastname].filter(Boolean).join(' ');
  const line = `Firmado por delegación: ${assistant} en nombre de Dr. ${doctor?.first_name ?? ''} ${doctor?.last_name ?? ''}`;
  const when = formatSignedAt(signed.signed_at);
  return when ? `${line} — ${when}` : line;
}

/**
 * The signature a prescription prints under: its own once re-issued (only
 * re-issued rows carry one), otherwise its consultation's. Mirrors
 * `pdf/partials/prescription-delegation-row.blade.php`.
 */
export function effectiveSignature<T extends SignatureFields>(prescription: SignatureFields, consultation: T): SignatureFields | T {
  return prescription.signature_method != null ? prescription : consultation;
}
